import React, { useEffect, useRef, useState } from "react";
import styles from "./HeroSection.module.css";
import { HERO_HEXAGON } from "../assets";
import Fancybox from "../../Culture/ImgSection/Fancybox";
import HEX_PREVIEW from "../../../assets/images/application/MCVideo.mp4";

// The overview video. Every other play control on the site opens the YouTube
// /embed/ URL through Fancybox, so this one does the same.
const OVERVIEW_VIDEO = "https://www.youtube.com/embed/p7vc1C2LrWg";

const STATS = [
  { prefix: "+", value: "3", suffix: "%", label: "Throughput Increased" },
  { prefix: "~", value: "95", suffix: "%", label: "Reduction in Alloy Waste" },
  { prefix: "", value: "50", suffix: "%", label: "Faster Shopfloor Decisions" },
  { prefix: "", value: "5", suffix: "%", label: "Energy Cost Savings" },
];

export default function HeroSection({ onBookDemo }) {
  const previewRef = useRef(null);
  // The clip is a few megabytes, so for the first seconds of a visit it has no
  // frame to show. Its letterbox bars are opaque, so fading it in before then
  // turns the hexagon into a black slab - hence the gate: nothing fades until
  // the video can actually paint.
  const [previewReady, setPreviewReady] = useState(false);

  // Chrome will not decode a video it is painting at opacity 0 - even an
  // explicit load() leaves it at readyState 0 - so the video is the layer that
  // stays visible and the still is the one that fades, with the video simply
  // covered until then. load() still runs so the clip is ready before the
  // first hover rather than during it.
  useEffect(() => {
    previewRef.current?.load();
  }, []);

  // Same treatment as /metalcloud in production: a muted loop sits over the
  // still at opacity 0 and fades in while the pointer is on the hexagon. The
  // clip is 16:9 and the slot is square, so it is letterboxed on black inside
  // the hexagon rather than cropped - cover would cut the title off at both
  // sides.
  const playPreview = () => {
    previewRef.current?.play().catch(() => {});
  };

  const stopPreview = () => {
    const video = previewRef.current;
    if (!video) return;
    video.pause();
    video.currentTime = 0;
  };

  const hexagon = (
    <Fancybox>
      <a
        href={OVERVIEW_VIDEO}
        data-fancybox
        className={`${styles.hexButton}${
          previewReady ? ` ${styles.hexButtonReady}` : ""
        }`}
        aria-label="Play the MetalCloud overview video"
        onMouseEnter={playPreview}
        onMouseLeave={stopPreview}
        onFocus={playPreview}
        onBlur={stopPreview}
      >
        <img
          src={HERO_HEXAGON}
          alt="Foundry operator using MetalCloud on a tablet, monitored by shopfloor cameras"
          className={styles.hexImage}
          width={588}
          height={588}
        />
        <video
          ref={previewRef}
          className={styles.hexVideo}
          muted
          loop
          playsInline
          preload="auto"
          tabIndex={-1}
          aria-hidden="true"
          onCanPlay={() => setPreviewReady(true)}
        >
          <source src={HEX_PREVIEW} type="video/mp4" />
        </video>
      </a>
    </Fancybox>
  );

  return (
    <section className={styles.section}>
      <div className={styles.container}>
        <div className={styles.inner}>
          <div className={styles.left}>
            <h1 className={styles.title}>
              <span className={styles.titleLight}>Your Entire Shop Floor On</span>
              <span className={styles.titleBold}>One AI Execution System</span>
            </h1>

            <p className={styles.subtitle}>
              MetalCloud is an AI-first execution system for foundries,
              connecting real-time shopfloor data to deliver verified execution,
              predictable outcomes, and faster operational decision-making.
            </p>

            <button type="button" className={styles.cta} onClick={onBookDemo}>
              Book a Demo Today
            </button>

            <ul className={styles.stats}>
              {STATS.map((stat) => (
                <li key={stat.label} className={styles.statCard}>
                  <p className={styles.statValue}>
                    {stat.prefix && (
                      <span className={styles.statAffix}>{stat.prefix}</span>
                    )}
                    <span className={styles.statNumber}>{stat.value}</span>
                    <span className={styles.statAffix}>{stat.suffix}</span>
                  </p>
                  <p className={styles.statLabel}>{stat.label}</p>
                </li>
              ))}
            </ul>
          </div>

          <div className={styles.right}>{hexagon}</div>
        </div>
      </div>
    </section>
  );
}
